import React, { useContext, useEffect, useMemo, useState } from "react";
import {
	Box,
	Button,
	Chip,
	Container,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControl,
	Grid,
	IconButton,
	InputLabel,
	LinearProgress,
	MenuItem,
	Paper,
	Select,
	Tab,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Tabs,
	TextField,
	Tooltip,
	Typography
} from "@material-ui/core";
import HelpOutlineIcon from "@material-ui/icons/HelpOutline";
import { makeStyles } from "@material-ui/core/styles";
import {
	Area,
	AreaChart,
	Bar,
	BarChart,
	CartesianGrid,
	ResponsiveContainer,
	Tooltip as ReTooltip,
	XAxis,
	YAxis
} from "recharts";

import { AuthContext } from "../../context/Auth/AuthContext";
import MainContainer from "../../components/MainContainer";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles(theme => ({
	mainPaper: {
		flex: 1,
		minHeight: 0,
		paddingTop: 10,
		overflowY: "auto",
		backgroundColor: theme.palette.background.paper,
		borderRadius: "18px 0 0 0",
		border: "none",
		boxShadow: "none",
		...theme.scrollbarStyles,
		[theme.breakpoints.down("sm")]: {
			borderRadius: 0
		}
	},
	container: {
		paddingTop: theme.spacing(1),
		paddingBottom: theme.spacing(3)
	},
	topbar: {
		marginBottom: theme.spacing(2)
	},
	title: {
		fontWeight: 700,
		fontSize: "1.2rem"
	},
	filtersWrap: {
		display: "flex",
		gap: theme.spacing(1),
		flexWrap: "wrap",
		justifyContent: "flex-end",
		[theme.breakpoints.down("sm")]: {
			justifyContent: "flex-start"
		}
	},
	filterControl: {
		minWidth: 130,
		flex: "1 1 150px"
	},
	filterNote: {
		color: theme.palette.text.secondary,
		fontSize: "0.78rem",
		marginTop: theme.spacing(0.75),
		textAlign: "right",
		[theme.breakpoints.down("sm")]: {
			textAlign: "left"
		}
	},
	tabs: {
		minHeight: 40,
		marginBottom: theme.spacing(2),
		borderBottom: `1px solid ${theme.palette.divider}`
	},
	card: {
		padding: theme.spacing(2),
		borderRadius: 14,
		height: "100%",
		border: `1px solid ${theme.palette.divider}`,
		boxShadow: "none"
	},
	kpiValue: {
		fontSize: 32,
		fontWeight: 800,
		marginTop: theme.spacing(1)
	},
	kpiSub: {
		color: theme.palette.text.secondary
	},
	sectionTitle: {
		fontWeight: 700,
		marginBottom: theme.spacing(1.5)
	},
	rankingRow: {
		display: "grid",
		gridTemplateColumns: "minmax(0, 1fr) auto auto auto",
		alignItems: "center",
		gap: theme.spacing(1),
		padding: theme.spacing(1, 0),
		borderBottom: `1px solid ${theme.palette.divider}`,
		"&:last-child": {
			borderBottom: "none"
		}
	},
	rankingLabel: {
		whiteSpace: "nowrap",
		overflow: "hidden",
		textOverflow: "ellipsis"
	},
	tableWrap: {
		border: `1px solid ${theme.palette.divider}`,
		borderRadius: 14,
		overflowX: "auto",
		boxShadow: "none"
	},
	table: {
		width: "max-content",
		minWidth: "100%",
		tableLayout: "auto"
	},
	tableHeadCell: {
		fontWeight: 700,
		padding: theme.spacing(1, 1.75),
		whiteSpace: "nowrap",
		backgroundColor: theme.palette.background.default
	},
	tableCell: {
		padding: theme.spacing(1, 1.75),
		whiteSpace: "nowrap"
	},
	tableNameCell: {
		maxWidth: 220,
		overflow: "hidden",
		textOverflow: "ellipsis"
	},
	metricStrong: {
		fontWeight: 700
	},
	listRow: {
		display: "grid",
		gridTemplateColumns: "minmax(0, 1fr) auto",
		gap: theme.spacing(1),
		padding: theme.spacing(1, 0),
		borderBottom: `1px solid ${theme.palette.divider}`,
		"&:last-child": {
			borderBottom: "none"
		}
	},
	helpText: {
		color: theme.palette.text.secondary,
		lineHeight: 1.6
	},
	emptyText: {
		color: theme.palette.text.secondary
	}
}));

const CONNECTION_ALL = "all";
const PERIOD_TODAY = "today";
const PERIOD_YESTERDAY = "yesterday";
const PERIOD_THIS_WEEK = "thisWeek";
const PERIOD_LAST_WEEK = "lastWeek";
const PERIOD_THIS_MONTH = "thisMonth";
const PERIOD_LAST_MONTH = "lastMonth";
const PERIOD_CUSTOM = "custom";

const GLOBAL_INDICATOR_KEYS = [
	"health",
	"waitingForService",
	"inService",
	"unanswered",
	"averageWait",
	"longestWait",
	"busiestAgent",
	"busiestArea"
];

const getStartOfDay = date => {
	const value = new Date(date);
	value.setHours(0, 0, 0, 0);
	return value;
};

const getEndOfDay = date => {
	const value = new Date(date);
	value.setHours(23, 59, 59, 999);
	return value;
};

const getStartOfWeekMonday = date => {
	const value = getStartOfDay(date);
	const day = value.getDay();
	const diff = day === 0 ? -6 : 1 - day;
	value.setDate(value.getDate() + diff);
	return value;
};

const toInputDate = date => {
	const year = date.getFullYear();
	const month = String(date.getMonth() + 1).padStart(2, "0");
	const day = String(date.getDate()).padStart(2, "0");
	return `${year}-${month}-${day}`;
};

const formatWaitLabel = totalMinutes => {
	const minutes = Math.max(0, Number(totalMinutes) || 0);
	if (minutes < 60) return `${minutes} min`;
	const hours = minutes / 60;
	return hours >= 10 ? `${Math.round(hours)} h` : `${hours.toFixed(1)} h`;
};

const numberValue = value => Number(value || 0);

const formatPercentage = value => `${numberValue(value).toFixed(1)}%`;

const getPeriodRange = (period, customStartDate, customEndDate) => {
	const now = new Date();
	let startDate = getStartOfDay(now);
	let endDate = getEndOfDay(now);

	if (period === PERIOD_YESTERDAY) {
		const yesterday = getStartOfDay(now);
		yesterday.setDate(yesterday.getDate() - 1);
		startDate = getStartOfDay(yesterday);
		endDate = getEndOfDay(yesterday);
	}

	if (period === PERIOD_THIS_WEEK) {
		startDate = getStartOfWeekMonday(now);
		endDate = getEndOfDay(now);
	}

	if (period === PERIOD_LAST_WEEK) {
		const thisWeekStart = getStartOfWeekMonday(now);
		endDate = new Date(thisWeekStart);
		endDate.setMilliseconds(-1);
		startDate = new Date(thisWeekStart);
		startDate.setDate(startDate.getDate() - 7);
	}

	if (period === PERIOD_THIS_MONTH) {
		startDate = new Date(now.getFullYear(), now.getMonth(), 1);
		endDate = getEndOfDay(now);
	}

	if (period === PERIOD_LAST_MONTH) {
		startDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
		endDate = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59, 999);
	}

	if (period === PERIOD_CUSTOM && customStartDate && customEndDate) {
		startDate = getStartOfDay(new Date(`${customStartDate}T00:00:00`));
		endDate = getEndOfDay(new Date(`${customEndDate}T00:00:00`));
	}

	return { startDate, endDate };
};

const Dashboard = () => {
	const classes = useStyles();
	const { user } = useContext(AuthContext);
	const tIndicators = (key, options) => i18n.t(`dashboard.indicators.${key}`, options);
	const globalIndicatorTitles = GLOBAL_INDICATOR_KEYS.reduce((acc, key) => {
		acc[key] = tIndicators(`global.${key}`);
		return acc;
	}, {});

	const userQueues = useMemo(() => (Array.isArray(user?.queues) ? user.queues : []), [user?.queues]);
	const userQueueIds = useMemo(() => userQueues.map(queue => Number(queue.id)), [userQueues]);

	const [tab, setTab] = useState("now");
	const [queueFilter, setQueueFilter] = useState("all");
	const [dayFilter, setDayFilter] = useState(PERIOD_TODAY);
	const [connectionFilter, setConnectionFilter] = useState(CONNECTION_ALL);
	const [connections, setConnections] = useState([]);
	const [customStartDate, setCustomStartDate] = useState("");
	const [customEndDate, setCustomEndDate] = useState("");
	const [dashboardData, setDashboardData] = useState(null);
	const [loading, setLoading] = useState(false);
	const [helpConfig, setHelpConfig] = useState(null);
	const [refreshTrigger, setRefreshTrigger] = useState(0);
	const isNowTab = tab === "now";

	useEffect(() => {
		const interval = setInterval(() => setRefreshTrigger(prev => prev + 1), 15000);
		return () => clearInterval(interval);
	}, []);

	useEffect(() => {
		let isMounted = true;
		const loadConnections = async () => {
			try {
				const { data } = await api.get("/whatsapp/");
				if (!isMounted) return;
				const parsedConnections = Array.isArray(data)
					? data
							.filter(item => Number(item?.id) > 0)
							.map(item => ({ id: Number(item.id), name: String(item.name || tIndicators("fallback.connection", { id: item.id })) }))
							.sort((a, b) => a.name.localeCompare(b.name, "pt-BR"))
					: [];
				setConnections(parsedConnections);
			} catch (err) {
				if (isMounted) toastError(err);
			}
		};
		loadConnections();
		return () => {
			isMounted = false;
		};
	}, []);

	const activeQueueIds = useMemo(() => {
		if (queueFilter === "all") return userQueueIds;
		return [Number(queueFilter)];
	}, [queueFilter, userQueueIds]);

	const selectedConnectionId = useMemo(() => {
		if (connectionFilter === CONNECTION_ALL) return null;
		const parsed = Number(connectionFilter);
		return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
	}, [connectionFilter]);

	const periodRange = useMemo(
		() => getPeriodRange(dayFilter, customStartDate, customEndDate),
		[customEndDate, customStartDate, dayFilter]
	);

	useEffect(() => {
		let isMounted = true;
		const loadDashboard = async () => {
			if (!isNowTab && dayFilter === PERIOD_CUSTOM && (!customStartDate || !customEndDate)) {
				setDashboardData(null);
				return;
			}

			setLoading(true);
			try {
				const { data } = await api.get("/tickets/dashboard-indicators", {
					params: {
						startDate: periodRange.startDate.toISOString(),
						endDate: periodRange.endDate.toISOString(),
						queueIds: JSON.stringify(activeQueueIds),
						...(selectedConnectionId ? { whatsappId: String(selectedConnectionId) } : {})
					}
				});
				if (isMounted) setDashboardData(data || null);
			} catch (err) {
				if (isMounted) {
					setDashboardData(null);
					toastError(err);
				}
			} finally {
				if (isMounted) setLoading(false);
			}
		};
		loadDashboard();
		return () => {
			isMounted = false;
		};
	}, [activeQueueIds, customEndDate, customStartDate, dayFilter, isNowTab, periodRange, refreshTrigger, selectedConnectionId]);

	const current = dashboardData?.current || {};
	const period = dashboardData?.period || {};
	const agents = Array.isArray(dashboardData?.agents) ? dashboardData.agents : [];
	const queues = Array.isArray(dashboardData?.queues) ? dashboardData.queues : [];
	const channelConnections = Array.isArray(dashboardData?.connections) ? dashboardData.connections : [];
	const lists = dashboardData?.lists || {};

	const openHelp = key => {
		const data = current[key];
		if (!data?.help) return;
		setHelpConfig({
			title: globalIndicatorTitles[key] || key,
			...data.help
		});
	};

	const renderHelpButton = key => (
		<Tooltip title={tIndicators("help.tooltip")}>
			<IconButton size="small" onClick={() => openHelp(key)}>
				<HelpOutlineIcon fontSize="small" />
			</IconButton>
		</Tooltip>
	);

	const renderKpi = ({ title, value, sub, helpKey }) => (
		<Grid item xs={12} sm={6} md={3}>
			<Paper className={classes.card}>
				<Box display="flex" justifyContent="space-between" alignItems="center">
					<Typography variant="subtitle2">{title}</Typography>
					{helpKey ? renderHelpButton(helpKey) : null}
				</Box>
				<Typography className={classes.kpiValue}>{value}</Typography>
				{sub ? <Typography variant="body2" className={classes.kpiSub}>{sub}</Typography> : null}
			</Paper>
		</Grid>
	);

	const renderRanking = (items, columns, emptyKey = "empty.noData") => (
		<Paper className={classes.card}>
			{items.length === 0 ? (
				<Typography className={classes.emptyText}>{tIndicators(emptyKey)}</Typography>
			) : (
				items.map((item, index) => (
					<Box className={classes.rankingRow} key={`${item.id || item.name}-${index}`}>
						<Typography className={classes.rankingLabel} variant="body2">{item.name}</Typography>
						{columns.map(column => (
							<Typography key={column.key} variant="body2" align="right">
								<strong>{column.format ? column.format(item[column.key], item) : item[column.key]}</strong>
								{column.label ? ` ${column.label}` : ""}
							</Typography>
						))}
					</Box>
				))
			)}
		</Paper>
	);

	const renderMetricTable = ({ items, nameHeader, columns, emptyKey = "empty.noData" }) => (
		<TableContainer component={Paper} className={classes.tableWrap}>
			<Table className={classes.table} size="small">
				<TableHead>
					<TableRow>
						<TableCell className={`${classes.tableHeadCell} ${classes.tableNameCell}`}>{nameHeader}</TableCell>
						{columns.map(column => (
							<TableCell key={column.key} align={column.align || "right"} className={classes.tableHeadCell}>
								{column.label}
							</TableCell>
						))}
					</TableRow>
				</TableHead>
				<TableBody>
					{items.length === 0 ? (
						<TableRow>
							<TableCell className={classes.tableCell} colSpan={columns.length + 1}>
								<Typography className={classes.emptyText}>{tIndicators(emptyKey)}</Typography>
							</TableCell>
						</TableRow>
					) : (
						items.map((item, index) => (
							<TableRow key={`${item.id || item.name}-${index}`}>
								<TableCell className={`${classes.tableCell} ${classes.tableNameCell}`}>
									<Tooltip title={item.name || ""}>
										<Typography variant="body2" className={classes.rankingLabel}>{item.name}</Typography>
									</Tooltip>
								</TableCell>
								{columns.map(column => (
									<TableCell key={column.key} align={column.align || "right"} className={classes.tableCell}>
										<span className={classes.metricStrong}>
											{column.format ? column.format(item[column.key], item) : numberValue(item[column.key])}
										</span>
									</TableCell>
								))}
							</TableRow>
						))
					)}
				</TableBody>
			</Table>
		</TableContainer>
	);

	const renderActionList = (title, items) => (
		<Paper className={classes.card}>
			<Typography className={classes.sectionTitle}>{title}</Typography>
			{!items || items.length === 0 ? (
				<Typography className={classes.emptyText}>{tIndicators("empty.noData")}</Typography>
			) : (
				items.map(item => (
					<Box className={classes.listRow} key={`${title}-${item.id}-${item.updatedAt}`}>
						<Box minWidth={0}>
							<Typography variant="body2" className={classes.rankingLabel}>
								#{item.id} {item.contactName}
							</Typography>
							<Typography variant="caption" color="textSecondary">
								{item.queueName || item.connectionName || item.status}
							</Typography>
						</Box>
						<Chip size="small" label={item.waitMinutes ? formatWaitLabel(item.waitMinutes) : item.status} />
					</Box>
				))
			)}
		</Paper>
	);

	return (
		<MainContainer>
			<div className={classes.mainPaper}>
				<Container maxWidth="lg" className={classes.container}>
					<Grid container spacing={2} className={classes.topbar} alignItems="center">
						<Grid item xs={12} md={4}>
							<Typography variant="h5" className={classes.title}>{tIndicators("title")}</Typography>
						</Grid>
						<Grid item xs={12} md={8}>
							<Box>
								<Box className={classes.filtersWrap}>
									{!isNowTab ? (
										<FormControl variant="outlined" size="small" className={classes.filterControl}>
											<InputLabel>{tIndicators("filters.period")}</InputLabel>
											<Select value={dayFilter} onChange={event => setDayFilter(event.target.value)} label={tIndicators("filters.period")}>
												<MenuItem value={PERIOD_TODAY}>{tIndicators("periods.today")}</MenuItem>
												<MenuItem value={PERIOD_YESTERDAY}>{tIndicators("periods.yesterday")}</MenuItem>
												<MenuItem value={PERIOD_THIS_WEEK}>{tIndicators("periods.thisWeek")}</MenuItem>
												<MenuItem value={PERIOD_LAST_WEEK}>{tIndicators("periods.lastWeek")}</MenuItem>
												<MenuItem value={PERIOD_THIS_MONTH}>{tIndicators("periods.thisMonth")}</MenuItem>
												<MenuItem value={PERIOD_LAST_MONTH}>{tIndicators("periods.lastMonth")}</MenuItem>
												<MenuItem value={PERIOD_CUSTOM}>{tIndicators("periods.custom")}</MenuItem>
											</Select>
										</FormControl>
									) : null}
									{!isNowTab && dayFilter === PERIOD_CUSTOM ? (
									<>
										<TextField
											variant="outlined"
											size="small"
											type="date"
											label={tIndicators("filters.start")}
											className={classes.filterControl}
											value={customStartDate}
											onChange={event => setCustomStartDate(event.target.value)}
											InputLabelProps={{ shrink: true }}
											inputProps={{ max: customEndDate || undefined }}
										/>
										<TextField
											variant="outlined"
											size="small"
											type="date"
											label={tIndicators("filters.end")}
											className={classes.filterControl}
											value={customEndDate}
											onChange={event => setCustomEndDate(event.target.value)}
											InputLabelProps={{ shrink: true }}
											inputProps={{ min: customStartDate || undefined, max: toInputDate(new Date()) }}
										/>
									</>
								) : null}
									<FormControl variant="outlined" size="small" className={classes.filterControl}>
										<InputLabel>{tIndicators("filters.queue")}</InputLabel>
										<Select value={queueFilter} onChange={event => setQueueFilter(event.target.value)} label={tIndicators("filters.queue")}>
											<MenuItem value="all">{tIndicators("filters.allQueues")}</MenuItem>
											{userQueues.map(queue => (
												<MenuItem key={queue.id} value={String(queue.id)}>{queue.name}</MenuItem>
											))}
										</Select>
									</FormControl>
									<FormControl variant="outlined" size="small" className={classes.filterControl}>
										<InputLabel>{tIndicators("filters.connection")}</InputLabel>
										<Select value={connectionFilter} onChange={event => setConnectionFilter(event.target.value)} label={tIndicators("filters.connection")}>
											<MenuItem value={CONNECTION_ALL}>{tIndicators("filters.allConnections")}</MenuItem>
											{connections.map(connection => (
												<MenuItem key={connection.id} value={String(connection.id)}>{connection.name}</MenuItem>
											))}
										</Select>
									</FormControl>
								</Box>
								<Typography className={classes.filterNote}>
									{isNowTab ? tIndicators("filters.nowNote") : tIndicators("filters.periodNote")}
								</Typography>
							</Box>
						</Grid>
					</Grid>

					{loading ? <LinearProgress /> : null}

					<Tabs value={tab} onChange={(event, value) => setTab(value)} className={classes.tabs} variant="scrollable" scrollButtons="auto">
						<Tab value="now" label={tIndicators("tabs.now")} />
						<Tab value="period" label={tIndicators("tabs.period")} />
						<Tab value="agents" label={tIndicators("tabs.agents")} />
						<Tab value="queues" label={tIndicators("tabs.queues")} />
						<Tab value="connections" label={tIndicators("tabs.connections")} />
						<Tab value="lists" label={tIndicators("tabs.lists")} />
					</Tabs>

					{tab === "now" && (
						<Grid container spacing={2}>
							{renderKpi({
								title: globalIndicatorTitles.health,
								value: `${numberValue(current.health?.score)}%`,
								sub: current.health?.label,
								helpKey: "health"
							})}
							{renderKpi({ title: globalIndicatorTitles.waitingForService, value: numberValue(current.waitingForService?.value), helpKey: "waitingForService" })}
							{renderKpi({ title: globalIndicatorTitles.inService, value: numberValue(current.inService?.value), helpKey: "inService" })}
							{renderKpi({ title: globalIndicatorTitles.unanswered, value: numberValue(current.unanswered?.value), helpKey: "unanswered" })}
							{renderKpi({ title: globalIndicatorTitles.averageWait, value: formatWaitLabel(current.averageWait?.minutes), helpKey: "averageWait" })}
							{renderKpi({ title: globalIndicatorTitles.longestWait, value: formatWaitLabel(current.longestWait?.minutes), helpKey: "longestWait" })}
							{renderKpi({
								title: globalIndicatorTitles.busiestAgent,
								value: numberValue(current.busiestAgent?.activeConversations),
								sub: current.busiestAgent?.name || tIndicators("fallback.noAgent"),
								helpKey: "busiestAgent"
							})}
							{renderKpi({
								title: globalIndicatorTitles.busiestArea,
								value: numberValue(current.busiestArea?.waitingConversations),
								sub: current.busiestArea?.name || tIndicators("fallback.noDefinedQueue"),
								helpKey: "busiestArea"
							})}
						</Grid>
					)}

					{tab === "period" && (
						<Grid container spacing={2}>
							{renderKpi({ title: tIndicators("periodMetrics.started"), value: numberValue(period.started) })}
							{renderKpi({ title: tIndicators("periodMetrics.reopened"), value: numberValue(period.reopened) })}
							{renderKpi({ title: tIndicators("periodMetrics.closed"), value: numberValue(period.closed) })}
							{renderKpi({ title: tIndicators("periodMetrics.reopenRate"), value: `${numberValue(period.reopenRate)}%` })}
							{renderKpi({ title: tIndicators("periodMetrics.firstResponse"), value: formatWaitLabel(period.firstResponseAverageMinutes) })}
							{renderKpi({ title: tIndicators("periodMetrics.resolution"), value: formatWaitLabel(period.resolutionAverageMinutes) })}
							{renderKpi({ title: tIndicators("periodMetrics.inbound"), value: numberValue(period.inboundMessages) })}
							{renderKpi({ title: tIndicators("periodMetrics.outbound"), value: numberValue(period.outboundMessages) })}
							<Grid item xs={12} md={8}>
								<Paper className={classes.card}>
									<Typography className={classes.sectionTitle}>{tIndicators("sections.flow")}</Typography>
									<Box height={280}>
										<ResponsiveContainer>
											<AreaChart data={period.flowByHour || []}>
												<CartesianGrid strokeDasharray="3 3" />
												<XAxis dataKey="hour" />
												<YAxis allowDecimals={false} />
												<ReTooltip />
												<Area type="monotone" dataKey="value" name={tIndicators("periodMetrics.started")} stroke="#2563eb" fill="#2563eb" fillOpacity={0.2} />
											</AreaChart>
										</ResponsiveContainer>
									</Box>
								</Paper>
							</Grid>
							<Grid item xs={12} md={4}>
								<Paper className={classes.card}>
									<Typography className={classes.sectionTitle}>{tIndicators("periodMetrics.closedByHour")}</Typography>
									<Box height={280}>
										<ResponsiveContainer>
											<BarChart data={period.closedByHour || []}>
												<CartesianGrid strokeDasharray="3 3" />
												<XAxis dataKey="hour" />
												<YAxis allowDecimals={false} />
												<ReTooltip />
												<Bar dataKey="value" fill="#16a34a" name={tIndicators("periodMetrics.closed")} />
											</BarChart>
										</ResponsiveContainer>
									</Box>
								</Paper>
							</Grid>
						</Grid>
					)}

					{tab === "agents" && (
						<Grid container spacing={2}>
							<Grid item xs={12}>
								<Typography className={classes.sectionTitle}>{tIndicators("sections.agents")}</Typography>
								{renderMetricTable({
									items: agents,
									nameHeader: tIndicators("table.agent"),
									columns: [
										{ key: "attendantType", label: tIndicators("table.agentType"), format: value => value === "ai_agent" ? tIndicators("attendantType.aiAgent") : value === "human" ? tIndicators("attendantType.human") : tIndicators("attendantType.unassigned") },
										{ key: "active", label: tIndicators("table.active") },
										{ key: "closed", label: tIndicators("table.closed") },
										{ key: "closedPercentage", label: tIndicators("table.closedPercentage"), format: formatPercentage },
										{ key: "sentMessages", label: tIndicators("table.sent") },
										{ key: "averageResolutionMinutes", label: tIndicators("table.avgResolution"), format: value => formatWaitLabel(value) }
									]
								})}
							</Grid>
						</Grid>
					)}

					{tab === "queues" && (
						<Grid container spacing={2}>
							<Grid item xs={12}>
								<Typography className={classes.sectionTitle}>{tIndicators("sections.queues")}</Typography>
								{renderMetricTable({
									items: queues,
									nameHeader: tIndicators("table.queue"),
									columns: [
										{ key: "waiting", label: tIndicators("table.waiting") },
										{ key: "inService", label: tIndicators("table.inService") },
										{ key: "started", label: tIndicators("table.started") },
										{ key: "closed", label: tIndicators("table.closed") },
										{ key: "averageWaitMinutes", label: tIndicators("table.avgWait"), format: value => formatWaitLabel(value) }
									]
								})}
							</Grid>
						</Grid>
					)}

					{tab === "connections" && (
						<Grid container spacing={2}>
							<Grid item xs={12}>
								<Typography className={classes.sectionTitle}>{tIndicators("sections.connections")}</Typography>
								{renderMetricTable({
									items: channelConnections,
									nameHeader: tIndicators("table.connection"),
									columns: [
										{ key: "channel", label: tIndicators("table.channel"), format: value => value || "-" },
										{ key: "waiting", label: tIndicators("table.waiting") },
										{ key: "inService", label: tIndicators("table.inService") },
										{ key: "started", label: tIndicators("table.started") },
										{ key: "inboundMessages", label: tIndicators("table.inbound") },
										{ key: "closed", label: tIndicators("table.closed") }
									]
								})}
							</Grid>
						</Grid>
					)}

					{tab === "lists" && (
						<Grid container spacing={2}>
							<Grid item xs={12} md={6}>
								{renderActionList(tIndicators("actionLists.waitingLongest"), lists.waitingLongest)}
							</Grid>
							<Grid item xs={12} md={6}>
								{renderActionList(tIndicators("actionLists.unanswered"), lists.unanswered)}
							</Grid>
							<Grid item xs={12} md={6}>
								{renderActionList(tIndicators("actionLists.slaRisk"), lists.slaRisk)}
							</Grid>
							<Grid item xs={12} md={6}>
								{renderActionList(tIndicators("actionLists.reopened"), lists.reopened)}
							</Grid>
						</Grid>
					)}

					<Dialog open={Boolean(helpConfig)} onClose={() => setHelpConfig(null)} maxWidth="sm" fullWidth>
						<DialogTitle>{helpConfig?.title}</DialogTitle>
						<DialogContent>
							<Typography className={classes.helpText}>{helpConfig?.description}</Typography>
							<Box mt={2}>
								<Typography variant="subtitle2">{tIndicators("help.compositionTitle")}</Typography>
								<Typography className={classes.helpText}>{helpConfig?.composition}</Typography>
							</Box>
							<Box mt={2}>
								<Typography variant="subtitle2">{tIndicators("help.improveTitle")}</Typography>
								<Typography className={classes.helpText}>{helpConfig?.improve}</Typography>
							</Box>
						</DialogContent>
						<DialogActions>
							<Button onClick={() => setHelpConfig(null)} color="primary" variant="contained">{tIndicators("help.close")}</Button>
						</DialogActions>
					</Dialog>
				</Container>
			</div>
		</MainContainer>
	);
};

export default Dashboard;
